import React from 'react';
import PropTypes from 'prop-types';

import { ToastContext, WeezHeader, Button, Input, InputSublabel, InputLabel, Table, Dropdown, Checkbox } from '@weezevent/nacre';
import { requiredField, useForm, requiredEmailField } from '../../utils/validators';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { default as PartnerModel } from '../../models/partner';

import { Card } from '../../components/card';
import { Form, Select } from 'semantic-ui-react';
import { NoItems } from '../../components/no-items';
import i18n from 'i18next';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';

import PartnerUserModel from '../../models/partnerUser';
import PartnerRole from '../../models/partnerRole';
import DeletionModal from '../../components/deletionModal';

function isUniqueEmailError(e) {
    if (e?.code !== 400) {
        return false;
    }

    // We don't have a specific error code for that, so we check the message
    // It looks ugly but it's the only way to be sure
    // We can't use the backend error even with translation
    // Because we still should check is that the error is about email already exists
    if (e?.response?.email) {
        const lookup = 'email already exists';
        const checkLookup = msg => isString(msg) && msg.toLowerCase().includes(lookup);
        if (isString(e.response.email)) {
            return checkLookup(e.response.email);
        }
        if (isArray(e.response.email)) {
            return e.response.email.some(checkLookup);
        }
    }

    return false;
}

function PartnerUserAdd({ organizationId, partnerId, reload, roles, partner }) {
    /**
     * Form to add user in partner
     */
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            roles: requiredField,
            email: requiredEmailField
        };
    }, []);

    const [userForm, setUser, setUserObject, userErrors, userValidate] = useForm(
        {
            name: '',
            email: null,
            roles: [1],
            send_mail: false
        },
        validators
    );

    const rolesOptions = React.useMemo(() => {
        return roles.map(role => ({
            text: role.name,
            value: role.id,
            key: role.id
        }));
    }, [roles]);

    const onSubmit = React.useCallback(() => {
        if (userValidate()) {
            let partnerUser = new PartnerUserModel({
                ...userForm
            });

            partnerUser
                .save({ organizationId, partnerId })
                .then(({ response }) => {
                    reload();
                    toast.success(t(userForm.send_mail ? 'ticket.partners.users.create_success' : 'ticket.partners.users.create_success_without_email'));
                    return setUserObject({
                        name: '',
                        email: '',
                        roles: [1],
                        send_mail: userForm.send_mail
                    });
                })
                .catch(e => {
                    if (isUniqueEmailError(e)) {
                        toast.error(t('ticket.partners.users.create_error_unique_email'));
                    } else {
                        toast.error(t('ticket.partners.users.create_error'));
                    }
                });
        }
    }, [userForm, organizationId, partnerId, setUserObject, reload]);

    return (
        <div>
            <Form>
                <Form.Group>
                    <Form.Field style={{ width: '33%', maxWidth: '370px' }} error={userErrors['name'] ? true : false}>
                        <InputLabel label={t('ticket.partners.users.name')} />
                        <Input value={userForm.name} placeholder={t('ticket.partners.users.placeholder_name')} onChange={event => setUser('name', event.target.value)} />
                        {userErrors['name'] ? <label>{t(userErrors['name'])}</label> : null}
                    </Form.Field>
                    <Form.Field style={{ width: '33%', maxWidth: '370px' }} error={userErrors['email'] ? true : false}>
                        <InputLabel label={t('ticket.partners.users.email')} />
                        <Input value={userForm.email} placeholder={t('ticket.partners.users.placeholder_email')} onChange={event => setUser('email', event.target.value)} />
                        {userErrors['email'] ? <label>{t(userErrors['email'])}</label> : null}
                    </Form.Field>
                    <Form.Field error={userErrors['roles'] ? true : false}>
                        <InputLabel label={t('ticket.partners.users.role')} />
                        <Select
                            multiple
                            clearable
                            value={userForm.roles}
                            options={rolesOptions}
                            placeholder={t('ticket.partners.users.placeholder_role')}
                            onChange={(synth, data) => setUser('roles', data.value)}
                        />
                        {userErrors['roles'] ? <label>{t(userErrors['roles'])}</label> : null}
                    </Form.Field>
                </Form.Group>
            </Form>
            <div style={{ display: 'flex' }}>
                <Button
                    disabled={partner?.isDeleted()}
                    primary
                    inverted
                    label={userForm.send_mail ? t('ticket.partners.users.add_user_button_w_mail') : t('ticket.partners.users.add_user_button')}
                    onClick={onSubmit}
                />
                <span style={{ display: 'inline-block', marginLeft: '15px' }}>
                    <InputLabel label={t('ticket.partners.users.send_mail')} />
                    <Checkbox
                        checked={userForm.send_mail}
                        onChange={event => {
                            setUser('send_mail', event.target.checked);
                        }}
                    />
                </span>
            </div>
        </div>
    );
}
PartnerUserAdd.propTypes = {
    organizationId: PropTypes.string.isRequired,
    partnerId: PropTypes.string.isRequired,
    reload: PropTypes.func.isRequired,
    roles: PropTypes.object.isRequired
};

function PartnerUserTable({ userList, partnerId, organizationId, reload, roles, partner }) {
    /**
     * Display the table of parnter users
     */
    const { t } = useTranslation();
    const locale = i18n.language;
    const toast = React.useContext(ToastContext);
    const [userToDelete, setUser] = React.useState(null);

    const deleteUser = React.useCallback(
        user => {
            user.delete({ organizationId, partnerId })
                .then(() => {
                    setUser(null);
                    reload();
                    toast.success(t('ticket.partners.users.delete_success'));
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.partners.users.delete_error'));
                });
        },
        [partnerId, organizationId, reload]
    );

    const reloadToken = React.useCallback(
        user => {
            user.token = null;
            user.save({ organizationId, partnerId })
                .then(({ response }) => {
                    user.link = response.link;
                    toast.success(t('ticket.partners.users.generate_success'));
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.partners.users.generate_error'));
                });
        },
        [partnerId, organizationId, reload]
    );

    const copyLink = React.useCallback(
        user => {
            navigator.clipboard.writeText(user.link);
        },
        [partnerId, organizationId, reload]
    );

    const resend_mail = React.useCallback(
        (user, { mail_body = '', mail_subject = '' } = {}) => {
            user.resend_mail({
                organizationId,
                mail_body,
                mail_subject
            })
                .then(() => {
                    toast.success(t('ticket.partners.users.mail_success'));
                })
                .catch(e => {
                    toast.error(t('ticket.partners.users.mail_error'));
                });
        },
        [organizationId]
    );

    const format_date = date => {
        if (!date) {
            return '-';
        }
        return Intl.DateTimeFormat(locale).format(new Date(date));
    };

    if (!userList || !userList.length) {
        return <NoItems title={t('ticket.partners.users.no_users')} />;
    }

    return (
        <div>
            <DeletionModal
                onClose={() => setUser(null)}
                onAccept={() => {
                    deleteUser(userToDelete);
                }}
                objectToDelete={userToDelete}
                labelTitle={t(`ticket.partners.users.deletionTitleModal`, { name: userToDelete?.name ? userToDelete?.name : '' })}
                labelButton={t(`ticket.common.action.delete`)}
            />
            <InputSublabel label={t('ticket.partners.users.resume_users', { count: userList?.length })} style={{ display: 'flex', justifyContent: 'flex-end' }} />
            <Table>
                <Table.Header>
                    <Table.HeaderRow>
                        <Table.HeaderCell>{t('ticket.partners.users.user_identity')}</Table.HeaderCell>
                        <Table.HeaderCell>{t('ticket.partners.users.user_email')}</Table.HeaderCell>
                        <Table.HeaderCell>{t('ticket.partners.users.user_role')}</Table.HeaderCell>
                        <Table.HeaderCell>{t('ticket.partners.users.user_creation_date')}</Table.HeaderCell>
                        <Table.HeaderCell>{t('ticket.partners.users.user_invitation_date')}</Table.HeaderCell>
                        {/*<Table.HeaderCell>{t('ticket.partners.users.user_last_activity')}</Table.HeaderCell>*/}
                        <Table.HeaderCell>{t('ticket.common.actions')}</Table.HeaderCell>
                    </Table.HeaderRow>
                </Table.Header>
                <Table.Body>
                    {userList.map((user, index) => (
                        <Table.Row key={index}>
                            <Table.Cell content={user.name} />
                            <Table.Cell content={user.email} />
                            <Table.Cell
                                content={user.roles.map(role => {
                                    let partnerRole = roles.find(partnerRole => partnerRole.id === role);
                                    if (!partnerRole) {
                                        return null;
                                    }
                                    return partnerRole.name;
                                })}
                            />
                            <Table.Cell content={format_date(user.created)} />
                            <Table.Cell
                                content={format_date(
                                    user.mails.sort((mail1, mail2) => {
                                        // Most recent first
                                        return new Date(mail2.created) - new Date(mail1.created);
                                    })[0]?.created
                                )}
                            />
                            {/*<Table.Cell content={' '} />*/}
                            <Table.CellDropdown disabled={partner?.isDeleted()}>
                                <Dropdown.Item item={t('ticket.partners.users.copy_link')} onClick={() => copyLink(user)} />
                                <Dropdown.Item item={t('ticket.partners.users.resend_mail')} onClick={() => resend_mail(user)} />
                                <Dropdown.Line />
                                <Dropdown.Item item={t('ticket.partners.users.refresh_token')} onClick={() => reloadToken(user)} />
                                <Dropdown.Line />
                                <Dropdown.Item item={t('ticket.partners.users.delete')} onClick={() => setUser(user)} deleteItem />
                            </Table.CellDropdown>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </div>
    );
}
PartnerUserTable.propTypes = {
    organizationId: PropTypes.string.isRequired,
    partnerId: PropTypes.string.isRequired,
    reload: PropTypes.func.isRequired,
    userList: PropTypes.object.isRequired,
    roles: PropTypes.object.isRequired
};

export default function PartnerUser({ organization, partnerId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [partnerUserList, setPartnerUserList] = React.useState([]);
    const [partnersRole, setPartnersRole] = React.useState([]);
    const [partner, setPartner] = React.useState(null);

    React.useEffect(() => {
        if (partnerId) {
            let users = PartnerUserModel.getAll({ organizationId: organization.id, partnerId: partnerId });
            let roles = PartnerRole.getAll({ organizationId: organization.id, partnerId: partnerId });
            let partner = PartnerModel.get({ id: partnerId, organizationId: organization.id });

            Promise.all([users, roles, partner])
                .then(([users, roles, partner]) => {
                    setPartnerUserList(users);
                    setPartnersRole(roles);
                    setPartner(partner);
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.partners.retrieve.error'));
                });
        }
    }, [partnerId, organization]);

    const reload = React.useCallback(() => {
        PartnerUserModel.getAll({
            organizationId: organization.id,
            partnerId: partnerId
        })
            .then(partnerUsers => {
                setPartnerUserList(partnerUsers);
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.partners.users.error_retrieve_data'));
            });
    }, [organization, partnerId]);

    if (!partnerId) {
        return null;
    }

    return (
        <React.Fragment>
            <WeezHeader title={t('ticket.partners.users.header')} backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/partners`) }} />
            <Card title={t('ticket.partners.users.title')}>
                <PartnerUserAdd organizationId={organization.id} partnerId={partnerId} reload={reload} roles={partnersRole} partner={partner} />
                <PartnerUserTable userList={partnerUserList} organizationId={organization.id} partnerId={partnerId} reload={reload} roles={partnersRole} partner={partner} />
            </Card>
        </React.Fragment>
    );
}

PartnerUser.propTypes = {
    organization: PropTypes.object.isRequired,
    partnerId: PropTypes.string.isRequired
};
